import React, { createContext, useContext, useState, useEffect } from "react";
import { isSupabaseConfigured, supabase } from "../lib/supabase";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!isSupabaseConfigured) {
      const storedUser = localStorage.getItem("blogUser");
      if (storedUser) {
        setUser(JSON.parse(storedUser));
      }
      setIsLoading(false);
      return undefined;
    }

    let isMounted = true;

    const mapSupabaseUser = (authUser) => {
      if (!authUser) {
        return null;
      }

      return {
        id: authUser.id,
        email: authUser.email,
        role: "admin",
        name:
          authUser.user_metadata?.full_name ||
          authUser.email ||
          "Administrator",
      };
    };

    const initializeAuth = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (isMounted) {
        setUser(mapSupabaseUser(session?.user));
        setIsLoading(false);
      }
    };

    initializeAuth();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (isMounted) {
        setUser(mapSupabaseUser(session?.user));
        setIsLoading(false);
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const login = async (identifier, password) => {
    if (isSupabaseConfigured) {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: identifier,
        password,
      });

      if (error) {
        return { success: false, error: error.message };
      }

      const authUser = data?.user;
      setUser({
        id: authUser.id,
        email: authUser.email,
        role: "admin",
        name:
          authUser.user_metadata?.full_name ||
          authUser.email ||
          "Administrator",
      });

      return { success: true };
    }

    const validCredentials = {
      username: "admin",
      password: "admin123",
    };

    if (
      identifier === validCredentials.username &&
      password === validCredentials.password
    ) {
      const userData = {
        username: identifier,
        role: "admin",
        name: "Administrator",
      };
      setUser(userData);
      localStorage.setItem("blogUser", JSON.stringify(userData));
      return { success: true };
    }

    return { success: false, error: "Invalid credentials" };
  };

  const logout = async () => {
    if (isSupabaseConfigured) {
      await supabase.auth.signOut();
      setUser(null);
      return;
    }

    setUser(null);
    localStorage.removeItem("blogUser");
  };

  const value = {
    user,
    login,
    logout,
    isLoading,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
